import { QueueListIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { Popover } from '@/components/Popover';
import { Button } from '@/components/ui/button';
import { useNow } from '@/hooks/useNow';
import { useBulkJobsStore } from '@/state/bulkJobs.store';

import { BulkJobRow } from './BulkJobRow';
import { BulkJobsBadge } from './BulkJobsBadge';

/**
 * BulkJobsDrawer — top-nav popover showing in-flight and recent bulk actions.
 *
 * Mirrors GlobalDownload.tsx composition:
 * - Popover + badge container + scrollable list
 * - Polls via useBulkJobsStore (fed by useBulkJobs hook from useBulkAction)
 *
 * UX divergences implemented:
 * #1 — amber ring (BulkJobsBadge uses chariot-status-warning)
 * #3 — badge never increments for suppressed (sub-2s) jobs
 * #4 — no completion toast; only failure toast links here
 */
export function BulkJobsDrawer() {
  const [open, setOpen] = useState(false);

  // Live timestamp for relative-time display; ticks only while the drawer is
  // open. Never compute Date.now() at render here — the React Compiler
  // memoizes it into a one-shot slot and freezes it at app load (ENG-5133).
  // 30s is enough: formatDate's coarsest visible transition is "now" →
  // "1 minute ago", so a bucket rollover shows at most ~30s late.
  const nowMs = useNow({ enabled: open, intervalMs: 30_000 });

  // B2: useShallow prevents re-renders when recentJobs() returns a new array
  // reference with identical content (e.g. during 3s active polling with no
  // actual job changes).
  const jobs = useBulkJobsStore(useShallow(state => state.recentJobs()));
  const activeCount = useBulkJobsStore(state => state.activeCount());
  const unviewedCount = useBulkJobsStore(state => state.unviewedCount);
  const clearUnviewed = useBulkJobsStore(state => state.clearUnviewed);
  const removeJob = useBulkJobsStore(state => state.removeJob);

  // B1: Listen for the custom event dispatched by useActiveBulkJobs toast
  // "View details" button so that clicking it opens this drawer and clears the
  // unviewed badge.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      clearUnviewed();
    };
    window.addEventListener('open-bulk-jobs-drawer', handler);
    return () => window.removeEventListener('open-bulk-jobs-drawer', handler);
  }, [clearUnviewed]);

  const hasFailure = jobs.some(
    j => j.status === 'fail' || j.status === 'partial'
  );

  function handleSetOpen(value: boolean) {
    setOpen(value);
    if (value) {
      clearUnviewed();
    }
  }

  const jobsExist = jobs.length > 0;

  return (
    <Popover
      open={open}
      setOpen={handleSetOpen}
      className="overflow-hidden p-0"
      placement="bottom"
      allowFlip={false}
      container={
        <div data-testid="bulk-jobs-drawer-icon">
          <Button
            variant="ghost"
            size="icon"
            className="group size-11"
            aria-label={
              activeCount
                ? `${activeCount} bulk action${activeCount === 1 ? '' : 's'} running`
                : 'Bulk actions'
            }
          >
            <BulkJobsBadge
              activeCount={activeCount}
              unviewedCount={unviewedCount}
              hasFailure={hasFailure}
            >
              <QueueListIcon className="text-chariot-text-secondary group-hover:text-chariot-text-primary size-6 transition-colors" />
            </BulkJobsBadge>
          </Button>
        </div>
      }
    >
      <div className="flex w-[380px] flex-col">
        {/* Sticky header */}
        <div className="border-chariot-stroke flex items-center justify-between border-b px-4 py-3">
          <p className="text-chariot-text-primary text-sm font-semibold">
            Bulk actions
          </p>
          {jobsExist && (
            <p className="text-chariot-text-secondary text-xs">
              {activeCount > 0 && `${activeCount} active · `}
              {
                jobs.filter(
                  j => j.status !== 'queued' && j.status !== 'running'
                ).length
              }{' '}
              recent
            </p>
          )}
        </div>

        {/* Scrollable content */}
        <div className="max-h-[400px] overflow-y-auto">
          {!jobsExist && (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <QueueListIcon className="text-chariot-text-secondary size-8" />
              <p className="text-chariot-text-secondary text-sm">
                No recent bulk actions
              </p>
            </div>
          )}
          <div
            className="divide-chariot-stroke divide-y"
            data-testid="bulk-job-list"
          >
            {jobs.map(job => (
              <BulkJobRow
                key={job.jobId}
                job={job}
                onDismiss={removeJob}
                nowMs={nowMs}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="border-chariot-stroke border-t px-4 py-2">
          <a
            href="#/jobs?source=bulk-action"
            className="text-chariot-brand-primary hover:text-chariot-brand-secondary text-xs underline"
          >
            View all jobs
          </a>
        </div>
      </div>
    </Popover>
  );
}
